import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_PeriodRow,
    WB_PlanManagementPeriodRow,
    WB_PlanManagementRow,
    WB_PlanManagementUserGroupRow,
    WB_PlanManagementUserRow,
    WB_ProductGroupRow,
    WB_SalesAreaRow,
    WB_UserGroupRow,
    WB_UserRow,
} from "../PA.Core/PAdbSchemaRows";
import { IwbPlanManagement } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Period,
    esWB_Plan,
    esWB_Plan_Funding_Metrics,
    esWB_Plan_Management,
    esWB_Plan_Management_Period,
    esWB_Plan_Management_User,
    esWB_Plan_Management_User_Group,
    esWB_Plan_Product_Group,
    esWB_Plan_Volume_Metrics,
    esWB_Product_Group,
    esWB_SalesArea,
    esWB_User,
    esWB_User_Group,
} from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PlanManagementBOValidations } from "./PlanManagementBO.validations";
import { PlanManagementBOAudit } from "./PlanManagementBO.Audit";
import { AuditService } from "../PA.Core/Security/AuditService";
import { PAEnvironment } from "../PA.Core/PAEnvironment";

export interface IPlanManagementLoadOptions {
    dbContext?: PAdbContext;
}

const PlanManagementLoadOptions_default: IPlanManagementLoadOptions = {};

export class PlanManagementBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PlanManagement: WB_PlanManagementRow;
    dctAuditedDeletes: { [id: number]: number };

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New PlanManagement" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_PlanManagement = undefined;
        this.dctAuditedDeletes = {};
    }

    //</editor-fold>

    get WB_PlanManagement(): WB_PlanManagementRow {
        return this.drWB_PlanManagement;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating PlanManagement");

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new PlanManagement.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext
                .createRecordWithKey(esWB_Plan_Management)
                .then((newPlanManagementRow: WB_PlanManagementRow) => {
                    newPlanManagementRow.setDefaults();
                    self.drWB_PlanManagement = newPlanManagementRow;
                    self.dbContext.add(esWB_Plan_Management, newPlanManagementRow);
                    self.Identity.setAll(
                        self.WB_PlanManagement.cif.WB_PLNMGMT_ID,
                        self.WB_PlanManagement.cif.PLNMGMT_ID,
                        self.WB_PlanManagement.cif.PLNMGMT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_PlanManagement,
                        "WB_PLNMGMT_ID",
                        "PLNMGMT_ID",
                        "PLNMGMT_NAME",
                    );

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        self._OnLoadComplete = internalOnComplete;

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve();
                })
                .then((ignored) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("PlanManagement already loaded.");
                resolve(self); // "PlanManagement already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, PlanManagementLoadOptions_default);

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for PlanManagement.");
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = PlanManagementBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = PlanManagementBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aPlanManagementRow: any = recordResolver.get(esWB_Plan_Management, anId);

                    if (_.isEmpty(aPlanManagementRow)) {
                        error("PlanManagement not Found");
                        return;
                    }

                    self.drWB_PlanManagement = aPlanManagementRow;
                    self.Identity.setAll(
                        self.WB_PlanManagement.cif.WB_PLNMGMT_ID,
                        self.WB_PlanManagement.cif.PLNMGMT_ID,
                        self.WB_PlanManagement.cif.PLNMGMT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_PlanManagement,
                        "WB_PLNMGMT_ID",
                        "PLNMGMT_ID",
                        "PLNMGMT_NAME",
                    );
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                    return;
                });
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_PlanManagement.cif.PLNMGMT_NAME + "(" + self.WB_PlanManagement.cif.PLNMGMT_ID + ")",
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate PlanManagement.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            PlanManagementBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbPlanManagement = <IwbPlanManagement>self.drWB_PlanManagement;
        let auditService: AuditService = undefined;
        this.SaveProgressProvider.start(
            "Saving " + wbPlanManagement.PLNMGMT_NAME + "(" + wbPlanManagement.PLNMGMT_ID + ")",
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save PlanManagement.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.SaveOptions = options || {};

            var saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            // Add code to do individual BO save for dbContextRows....
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }
                })
                .then((ignore) => {
                    return PlanManagementBOAudit.PlanManagementAuditBO(self);
                })
                .then((anAuditService) => {
                    auditService = anAuditService;
                    if (self.drWB_PlanManagement.cif.WB_SALESAREA_ID > 0)
                        return self.dbContext.find(esWB_SalesArea, self.drWB_PlanManagement.cif.WB_SALESAREA_ID);
                })
                .then((salesAreaRow: WB_SalesAreaRow) => {
                    self.drWB_PlanManagement.cif.WB_SALESORG_ID = salesAreaRow.cif.WB_SALESORG_ID;
                    return saveFunction(esWB_Plan_Management, self.drWB_PlanManagement, {});
                })
                .then((ignore) => {
                    let tasks: Promise<any>[] = [];
                    tasks.push(
                        saveFunction(esWB_Plan_Management_Period, self.drWB_PlanManagement.WB_PlanManagementPeriodRows),
                    );
                    tasks.push(
                        saveFunction(esWB_Plan_Management_User, self.drWB_PlanManagement.WB_PlanManagementUserRows),
                    );
                    tasks.push(
                        saveFunction(
                            esWB_Plan_Management_User_Group,
                            self.drWB_PlanManagement.WB_PlanManagementUserGroupRows,
                        ),
                    );
                    return Promise.all(tasks);
                })
                .then((ignored) => {
                    if (auditService) {
                        return auditService.Save(bBatch);
                    }
                })
                .then((ignored) => {
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbPlanManagement = <IwbPlanManagement>self.drWB_PlanManagement;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbPlanManagement.PLNMGMT_NAME + "(" + wbPlanManagement.WB_PLNMGMT_ID + ")",
        );
        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the PlanManagement

            // 2. If Valid, mark all the records for deletion.
            self.drWB_PlanManagement.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "Period Planning Level Operations">

    addPeriodPlanningLevel(aWBPeriodId: number, aWBPrdGrpId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            let aPeriodRow: WB_PeriodRow = undefined;
            let aPrdGrpRow: WB_ProductGroupRow = undefined;

            self.dbContext
                .find(esWB_Period, aWBPeriodId)
                .then((resPeriodRow) => {
                    if (_.isEmpty(resPeriodRow)) {
                        error("Unknown Period");
                        return;
                    }
                    aPeriodRow = resPeriodRow;
                    return self.dbContext.find(esWB_Product_Group, aWBPrdGrpId);
                })
                .then((resPrdGrpRow) => {
                    if (_.isEmpty(resPrdGrpRow)) {
                        error("Unknown Product Group");
                        return;
                    }
                    aPrdGrpRow = resPrdGrpRow;
                    return self.dbContext.createRecordWithKey(esWB_Plan_Management_Period);
                })
                .then((aNewRow: WB_PlanManagementPeriodRow) => {
                    if (_.isEmpty(aNewRow)) {
                        error("Unable to create new Row");
                        return;
                    }
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_PLNMGMT_ID = self.drWB_PlanManagement.cif.WB_PLNMGMT_ID;
                    aNewRow.cif.WB_PERIOD_ID = aPeriodRow.cif.WB_PERIOD_ID;
                    aNewRow.WB_PeriodRow = aPeriodRow;
                    aNewRow.cif.WB_PRDGRP_ID = aPrdGrpRow.cif.WB_PRDGRP_ID;
                    aNewRow.WB_ProductGroupRow = aPrdGrpRow;
                    aNewRow.cif.WB_PLANM_ID = 0;
                    self.dbContext.add(esWB_Plan_Management_Period, aNewRow);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removePeriodPlanningLevelRow(aWB_PLNMGMTR_ID: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(
                self.drWB_PlanManagement.WB_PlanManagementPeriodRows,
                (planManagementPeriodRow: WB_PlanManagementPeriodRow) => {
                    if (
                        planManagementPeriodRow.isActive &&
                        planManagementPeriodRow.cif.WB_PLNMGMTR_ID == aWB_PLNMGMTR_ID
                    ) {
                        planManagementPeriodRow.markDeleted();
                    }
                },
            );

            resolve(self);
        });
    }

    //</editor-fold>

    //<editor-fold desc = "Users Operations">

    addUser(aUserId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            let aUserRow: WB_UserRow = undefined;

            self.dbContext
                .find(esWB_User, aUserId)
                .then((aUserRecord) => {
                    aUserRow = aUserRecord;
                    return self.dbContext.createRecordWithKey(esWB_Plan_Management_User);
                })
                .then((aNewRow: WB_PlanManagementUserRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_PLNMGMT_ID = self.drWB_PlanManagement.cif.WB_PLNMGMT_ID;
                    aNewRow.cif.WB_AUTHUSR_ID = aUserRow.cif.WB_AUTHUSR_ID;
                    aNewRow.WB_AuthUserRow = aUserRow;
                    self.dbContext.add(esWB_Plan_Management_User, aNewRow);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeUser(aWB_PLNMGMTU_ID: number): Promise<IBusinessObject> {
        var self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(self.drWB_PlanManagement.WB_PlanManagementUserRows, (aUserRow: WB_PlanManagementUserRow) => {
                if (aUserRow.isActive && aUserRow.cif.WB_PLNMGMTU_ID == aWB_PLNMGMTU_ID) {
                    aUserRow.markDeleted();
                }
            });

            resolve(self);
        });
    }

    //</editor-fold>

    //<editor-fold desc = "User Group Operations">

    addUserGroup(aUserGroupId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            let aUserGroupRow: WB_UserGroupRow = undefined;

            self.dbContext
                .find(esWB_User_Group, aUserGroupId)
                .then((aUserGroupRecord) => {
                    aUserGroupRow = aUserGroupRecord;
                    return self.dbContext.createRecordWithKey(esWB_Plan_Management_User_Group);
                })
                .then((aNewRow: WB_PlanManagementUserGroupRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_PLNMGMT_ID = self.drWB_PlanManagement.cif.WB_PLNMGMT_ID;
                    aNewRow.cif.WB_USRGRP_ID = aUserGroupRow.cif.WB_USRGRP_ID;
                    //aNewRow.WB_UserGroupRow = aUserGroupRow
                    self.dbContext.add(esWB_Plan_Management_User_Group, aNewRow);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeUserGroup(aWB_PLNMGMTG_ID: number): Promise<IBusinessObject> {
        var self = this;

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(
                self.drWB_PlanManagement.WB_PlanManagementUserGroupRows,
                (aUserGroupRow: WB_PlanManagementUserGroupRow) => {
                    if (aUserGroupRow.isActive && aUserGroupRow.cif.WB_PLNMGMTG_ID == aWB_PLNMGMTG_ID) {
                        aUserGroupRow.markDeleted();
                    }
                },
            );

            resolve(self);
        });
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const plnmgmt_options: CreateOptions = {
            standardConditions: {
                [esWB_Period]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Funding_Metrics]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Management_Period]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Management_User]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Management_User_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Product_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Product_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_User]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_User_Group]: { DELETED_IND: { notIn: ["1"] } },
            },
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {
                [esWB_Plan]: [esWB_Plan_Funding_Metrics, esWB_Plan_Product_Group, esWB_Plan_Volume_Metrics],
            },
        };

        const aRecResolver = new ESRecordResolver(dbContext, plnmgmt_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Plan_Management, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold >
}
PAEnvironment.registerBusinessObjectClass(PlanManagementBO, "PlanManagementBO", "Plan Management");
